import { keyBy, mapValues } from 'lodash';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { Storage } from '@shared/util/storage';

export const STORAGE_PERSISTED_CHAT_MESSAGES_KEY = 'STORAGE:PERSISTED_CHAT_MESSAGES_KEY';

// Dict mapping channel slug to an array of chat messages
export type PersistedChatMessagesInStorage = Dict<API.ChatMessageFragment[]>;

/*
    Persist the current chat message content into store.
*/
export async function persist(state: MBStoreState) {
  const messages = state.chatMessages.cachedMessages;
  const uuidsFromChannel = state.chatMessages.cachedMessageUuidsFromChannelSlug;
  const statusFromUuid = state.chatMessages.deliveryStatusFromMessageUuid;

  // (1) loop over all channels with messages listed
  // (2) filter away all messages that we don't want to resend
  // (3) map to messages
  // (4) filter away messages that don't exist (shouldn't happen)
  const dataToPersist = Object.entries<string[]>(uuidsFromChannel).reduce((memo, [key, uuids]) => {
    const chatMessages = uuids
      .filter(uuid => ['waiting', 'failed', 'uploadingAttachments'].includes(statusFromUuid[uuid]))
      .map(uuid => messages[uuid])
      .filter(notNil);
    if (chatMessages.length > 0) {
      memo[key] = chatMessages;
    }
    return memo;
  }, {});

  Storage.setJSON(STORAGE_PERSISTED_CHAT_MESSAGES_KEY, dataToPersist);
}

/*
    Restores the data saved in setupListeningToChatMessagesToPersist
    from AsyncStorage. Note: this does not store it in redux.

    Note: we don't restore message attachments and if they've been uploaded.
    The reason is that we know which attachments have been uploaded by
    looking if their url is public (http) or local.
*/

type RestoreResult = {
  cachedMessages: Dict<API.ChatMessageFragment[]>;
  cachedMessageUuidsFromChannelSlug: Dict<string[]>;
  deliveryStatusFromMessageUuid: Dict<MBDeliveryStatus>;
};

export async function restore(): Promise<RestoreResult | undefined> {
  const persistedData = Storage.getJSON<PersistedChatMessagesInStorage>(
    STORAGE_PERSISTED_CHAT_MESSAGES_KEY,
  );

  if (!persistedData) {
    return undefined;
  }

  const persistedMessages = Object.values(persistedData).flat();
  const cachedMessages = keyBy(persistedMessages, 'meta.uuid');

  const cachedMessageUuidsFromChannelSlug = mapValues(
    persistedData,
    (messages: API.ChatMessageFragment[]): string[] =>
      messages.map(message => message.meta.uuid).filter(notNil),
  );

  const deliveryStatusFromMessageUuid = mapValues(cachedMessages, () => 'waiting');

  return {
    cachedMessages,
    cachedMessageUuidsFromChannelSlug,
    deliveryStatusFromMessageUuid,
  };
}
